import React, { useEffect } from "react";
import DockSlot from "./DockSlot";
import { CANVAS_ID_TO_SLOT, DOCK_SLOTS } from "./dockTypes";
import { setSlotLoader } from "./dockStore";

/**
 * Drop-in for <ImageCanvasCommon[New]/> on pages that share the single docked
 * visualizer. It renders NOTHING but a <DockSlot/> placeholder — the real,
 * single <canvas> lives in <VisualizerDockShell/> and overlays this slot.
 *
 * It intentionally does NOT push layers/size/canvasId anywhere: that input is
 * derived centrally in useDockedVisualizerInput() from the layout singleton +
 * Redux. So this component takes the same props the old canvas took (for a
 * painless swap) but only uses them to pick the slot.
 *
 * `enableLoader`/`disableLoader` are the one exception: they're genuinely
 * page-specific (materials' setMaterialsCanvasLoading, etc.), so they can't be
 * derived centrally the way layers/useMouseHovering are. Instead we register
 * whichever pair this page passed in against this slot (see dockStore's
 * dockLoadersStore); useDockedVisualizerInput() reads back the pair for
 * whichever slot is currently active and hands it to the single canvas
 * instance, so the right page's loading state gets toggled.
 *
 * Slot resolution: explicit `slotName` wins; otherwise the legacy `canvasId`
 * ("selectionCanvas" → materials, "shoppingCartDesktopCanvas" → cart) maps it;
 * otherwise the main visualizer slot.
 */
// forwardRef so the visualizers can pass `ref={(node) => this.imageCanvas = node}`
// without triggering React's "Function components cannot be given refs" warning.
// The docked canvas has no imperative instance to expose here (the real one lives
// in <VisualizerDockShell/> and is read via getVisualizerInstance()), so the ref
// stays null — the parent already falls back to the docked instance.
// eslint-disable-next-line no-unused-vars
const DockCanvas = React.forwardRef(function DockCanvas(props, ref) {
  // console.log("ref", ref);
  const { canvasId, slotName, className = "", enableLoader, disableLoader } = props;
  const resolvedSlot = slotName || CANVAS_ID_TO_SLOT[canvasId] || DOCK_SLOTS.visualizer;

  useEffect(() => {
    if (!enableLoader && !disableLoader) return undefined;
    setSlotLoader(resolvedSlot, { enableLoader, disableLoader });
    return () => setSlotLoader(resolvedSlot, null);
  }, [resolvedSlot, enableLoader, disableLoader]);

  return <DockSlot name={resolvedSlot} className={className} />;
});

export default DockCanvas;
